// =============================================================
// Reusable Chart.js chart components for the Training Institute
// analytics dashboard. Built on react-chartjs-2 (already in
// package.json). Each component handles its own registration of
// the required Chart.js modules and provides a consistent skin.
//
// All components are responsive (parent container controls width)
// and accept an optional `themeColor` so charts can use the
// tenant's brand primary colour rather than the default indigo.
//
// When a component receives no data, it still renders a "frame"
// (axes / grid) with a "no data" overlay so the layout is stable
// across the dashboard. Pass `frameOnly` = true to skip the
// overlay and just show the empty axes.
// =============================================================

import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';

// Register Chart.js modules exactly once. Chart.js v4 requires
// explicit registration of every component you use.
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

// Helpers — manipulate a hex colour without any external dep.
const hexToRgb = (hex) => {
  if (!hex) return null;
  let h = String(hex).trim().replace('#', '');
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  return { r: parseInt(h.slice(0, 2), 16), g: parseInt(h.slice(2, 4), 16), b: parseInt(h.slice(4, 6), 16) };
};
const rgba = (hex, alpha) => {
  const rgb = hexToRgb(hex);
  return rgb ? `rgba(${rgb.r},${rgb.g},${rgb.b},${alpha})` : hex;
};
// Returns a slightly darker shade for hover / border effects.
const shade = (hex, amount = 0.85) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const f = (c) => Math.max(0, Math.min(255, Math.round(c * amount)));
  return `rgb(${f(rgb.r)},${f(rgb.g)},${f(rgb.b)})`;
};

// Build a 4-step palette around the theme colour so stacked /
// series charts have visually distinct but harmonious bands.
const buildPalette = (base) => {
  const tints = [1, 0.78, 0.6, 0.45];
  return tints.map(t => rgba(base, t === 1 ? 0.92 : t));
};

// Theme tokens — `primary` is overridden per chart via the
// `themeColor` prop, but the neutral text / grid colours stay
// the same so the layout reads consistently.
export const THEME = {
  text:        '#475569',
  textMuted:   '#94a3b8',
  grid:        'rgba(148, 163, 184, 0.18)',
  tooltipBg:   'rgba(15, 23, 42, 0.92)',
  primary:     '#6366f1',
  success:     '#22c55e',
  danger:      '#ef4444',
  warning:     '#f59e0b',
  info:        '#0ea5e9',
  purple:      '#8b5cf6',
};

// ─── Common Chart.js options factory ─────────────────────────────
const commonOpts = (themeColor, extra = {}) => {
  const c = themeColor || THEME.primary;
  return {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: THEME.text,
          font: { size: 11, family: 'inherit' },
          boxWidth: 10,
          boxHeight: 10,
          padding: 12,
          usePointStyle: true,
          pointStyle: 'circle',
        },
      },
      tooltip: {
        backgroundColor: THEME.tooltipBg,
        titleColor: '#fff',
        bodyColor: '#fff',
        padding: 10,
        cornerRadius: 6,
        titleFont: { size: 12, weight: '700' },
        bodyFont: { size: 12 },
        borderColor: 'rgba(255,255,255,0.08)',
        borderWidth: 1,
      },
    },
    scales: {
      x: {
        grid: { color: THEME.grid, drawBorder: false },
        ticks: { color: THEME.textMuted, font: { size: 10 } },
      },
      y: {
        grid: { color: THEME.grid, drawBorder: false },
        ticks: { color: THEME.textMuted, font: { size: 10 } },
        beginAtZero: true,
      },
    },
    animation: { duration: 800, easing: 'easeOutQuart' },
    ...extra,
  };
};

// =============================================================
// LineChart
// =============================================================
export function LineChart({ labels = [], datasets = [], height = 220, yMax, yMin = 0, yStepSize, onPointClick, themeColor, tooltipTitle, xAxisTitle, yAxisTitle }) {
  const base = themeColor || THEME.primary;
  const palette = useMemo(() => buildPalette(base), [base]);

  const data = useMemo(() => ({
    labels,
    datasets: datasets.map((d, i) => ({
      label: d.label,
      data: d.data,
      borderColor: d.color || palette[i % palette.length],
      backgroundColor: d.fill ? rgba(d.color || palette[i % palette.length], 0.18) : (d.color || palette[i % palette.length]),
      fill: !!d.fill,
      tension: 0,
      pointRadius: d.pointRadius ?? 3,
      pointHoverRadius: 6,
      pointBackgroundColor: '#fff',
      pointBorderColor: d.color || palette[i % palette.length],
      pointBorderWidth: 2,
      borderWidth: 2.5,
    })),
  }), [labels, datasets, palette]);

  const opts = useMemo(() => commonOpts(base, {
    plugins: {
      ...commonOpts(base).plugins,
      tooltip: {
        ...commonOpts(base).plugins.tooltip,
        callbacks: tooltipTitle ? {
          title: (ctx) => tooltipTitle(ctx[0]?.dataIndex ?? 0) || ctx[0]?.label || '',
        } : undefined,
      },
    },
    scales: {
      x: {
        display: true,
        grid: { color: THEME.grid, drawBorder: false, display: true },
        ticks: { color: THEME.textMuted, font: { size: 10 }, autoSkip: false, maxRotation: 0 },
        title: xAxisTitle ? { display: true, text: xAxisTitle, color: THEME.text, font: { size: 11, weight: '700' } } : undefined,
      },
      y: {
        display: true,
        grid: { color: THEME.grid, drawBorder: false, display: true },
        ticks: { color: THEME.textMuted, font: { size: 10 }, stepSize: yStepSize, precision: 0 },
        beginAtZero: true, min: yMin, max: yMax,
        title: yAxisTitle ? { display: true, text: yAxisTitle, color: THEME.text, font: { size: 11, weight: '700' } } : undefined,
      },
    },
    onClick: (evt, elements) => {
      if (!onPointClick || !elements?.length) return;
      const idx = elements[0].index;
      onPointClick({ index: idx, label: labels[idx] });
    },
  }), [base, yMin, yMax, yStepSize, onPointClick, labels, tooltipTitle, xAxisTitle, yAxisTitle]);

  if (!labels.length || !datasets.length) return <EmptyChart height={height} />;
  return <div style={{ height }}><Line data={data} options={opts} /></div>;
}

// =============================================================
// BarChart
// =============================================================
export function BarChart({ labels = [], datasets = [], height = 220, horizontal = false, yMax, onBarClick, themeColor, tooltipTitle }) {
  const base = themeColor || THEME.primary;
  const palette = useMemo(() => buildPalette(base), [base]);

  const data = useMemo(() => ({
    labels,
    datasets: datasets.map((d, i) => ({
      label: d.label,
      data: d.data,
      backgroundColor: d.color || palette[i % palette.length],
      hoverBackgroundColor: d.color ? shade(d.color, 0.85) : shade(palette[i % palette.length], 0.85),
      borderRadius: 6,
      borderSkipped: false,
      maxBarThickness: 36,
    })),
  }), [labels, datasets, palette]);

  const opts = useMemo(() => {
    const baseOpts = commonOpts(base, {
      indexAxis: horizontal ? 'y' : 'x',
      plugins: {
        ...commonOpts(base).plugins,
        legend: { display: datasets.length > 1, ...commonOpts(base).plugins.legend },
        tooltip: {
          ...commonOpts(base).plugins.tooltip,
          callbacks: tooltipTitle ? {
            title: (ctx) => tooltipTitle(ctx[0]?.dataIndex ?? 0) || ctx[0]?.label || '',
          } : undefined,
        },
      },
      scales: {
        x: {
          grid: { color: THEME.grid, drawBorder: false },
          ticks: { color: THEME.textMuted, font: { size: 10 } },
          beginAtZero: true, max: yMax,
        },
        y: {
          grid: { color: THEME.grid, drawBorder: false },
          ticks: { color: THEME.textMuted, font: { size: 10 } },
          beginAtZero: true, max: yMax,
        },
      },
      onClick: (evt, elements) => {
        if (!onBarClick || !elements?.length) return;
        const idx = elements[0].index;
        onBarClick({ index: idx, label: labels[idx] });
      },
    });
    return baseOpts;
  }, [horizontal, yMax, onBarClick, labels, datasets.length, base, tooltipTitle]);

  if (!labels.length || !datasets.length) return <EmptyChart height={height} />;
  return <div style={{ height }}><Bar data={data} options={opts} /></div>;
}

// =============================================================
// DoughnutChart
// =============================================================
export function DoughnutChart({ slices = [], centerLabel, centerSubLabel, height = 200, onSliceClick, themeColor }) {
  const base = themeColor || THEME.primary;
  // Doughnut — build a palette from the theme colour when no per-slice
  // colour is provided so the chart feels native to the tenant brand.
  const themedPalette = useMemo(() => buildPalette(base), [base]);
  const computedSlices = useMemo(
    () => slices.map((s, i) => ({ ...s, color: s.color || themedPalette[i % themedPalette.length] })),
    [slices, themedPalette],
  );

  const data = useMemo(() => ({
    labels: computedSlices.map(s => s.label),
    datasets: [{
      data: computedSlices.map(s => s.value),
      backgroundColor: computedSlices.map(s => s.color),
      borderColor: '#fff',
      borderWidth: 2,
      hoverOffset: 8,
    }],
  }), [computedSlices]);

  const opts = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    cutout: '68%',
    animation: { animateRotate: true, animateScale: false, duration: 800 },
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: THEME.text,
          font: { size: 11, family: 'inherit' },
          boxWidth: 10, boxHeight: 10, padding: 10,
          usePointStyle: true, pointStyle: 'circle',
        },
      },
      tooltip: {
        backgroundColor: THEME.tooltipBg,
        titleColor: '#fff', bodyColor: '#fff',
        padding: 10, cornerRadius: 6,
        callbacks: {
          label: (ctx) => {
            const total = ctx.dataset.data.reduce((a, b) => a + b, 0) || 1;
            const pct = Math.round((ctx.parsed / total) * 100);
            return ` ${ctx.label}: ${ctx.parsed} (${pct}%)`;
          },
        },
      },
    },
    onClick: (evt, elements) => {
      if (!onSliceClick || !elements?.length) return;
      const idx = elements[0].index;
      onSliceClick({ index: idx, slice: computedSlices[idx] });
    },
  }), [onSliceClick, computedSlices]);

  if (!computedSlices.length) return <EmptyChart height={height} />;
  return (
    <div style={{ height, position: 'relative' }}>
      <Doughnut data={data} options={opts} />
      {(centerLabel || centerSubLabel) && (
        <div style={{
          position: 'absolute', top: '50%', left: '50%',
          transform: 'translate(-50%, -50%)', textAlign: 'center',
          pointerEvents: 'none',
        }}>
          {centerLabel && (
            <div style={{ fontSize: 22, fontWeight: 800, color: '#1e293b', lineHeight: 1.1 }}>
              {centerLabel}
            </div>
          )}
          {centerSubLabel && (
            <div style={{ fontSize: 11, color: THEME.textMuted, marginTop: 2 }}>
              {centerSubLabel}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// =============================================================
// MultiLineChart — convenience for stacked/parallel time series
// =============================================================
export function MultiLineChart({ labels = [], series = [], height = 240, onPointClick, themeColor }) {
  const datasets = series.map(s => ({
    label: s.label, data: s.data, color: s.color, fill: !!s.fill,
    pointRadius: 3,
  }));
  return <LineChart labels={labels} datasets={datasets} height={height} onPointClick={onPointClick} themeColor={themeColor} />;
}

// =============================================================
// StackedBarChart — present/absent/leave/permission
// Uses the theme colour for the primary band and step-down
// tints for the secondary / tertiary bands.
// =============================================================
export function StackedBarChart({ labels = [], series = [], height = 240, onBarClick, themeColor }) {
  const base = themeColor || THEME.primary;
  const palette = useMemo(() => buildPalette(base), [base]);

  const data = useMemo(() => ({
    labels,
    datasets: series.map((s, i) => ({
      label: s.label, data: s.data,
      backgroundColor: s.color || palette[i % palette.length],
      hoverBackgroundColor: s.color ? shade(s.color, 0.88) : shade(palette[i % palette.length], 0.88),
      borderRadius: 4,
      borderSkipped: false,
      maxBarThickness: 32,
    })),
  }), [labels, series, palette]);

  const opts = useMemo(() => commonOpts(base, {
    scales: {
      x: { stacked: true, grid: { display: false }, ticks: { color: THEME.textMuted, font: { size: 10 } } },
      y: { stacked: true, grid: { color: THEME.grid, drawBorder: false }, ticks: { color: THEME.textMuted, font: { size: 10 } }, beginAtZero: true },
    },
    onClick: (evt, elements) => {
      if (!onBarClick || !elements?.length) return;
      const idx = elements[0].index;
      onBarClick({ index: idx, label: labels[idx] });
    },
  }), [onBarClick, labels, base]);

  if (!labels.length || !series.length) return <EmptyChart height={height} />;
  return <div style={{ height }}><Bar data={data} options={opts} /></div>;
}

// =============================================================
// EmptyChart — shown when no data is available
// =============================================================
export function EmptyChart({ height = 220, label = 'No data available', themeColor }) {
  const base = themeColor || THEME.primary;
  return (
    <div style={{
      height, display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      color: THEME.textMuted, gap: 8,
      background: `linear-gradient(180deg, ${rgba(base, 0.03)} 0%, transparent 100%)`,
      border: `1px dashed ${rgba(base, 0.25)}`,
      borderRadius: 10,
    }}>
      <i className="bi bi-bar-chart-line" style={{ fontSize: 28, color: rgba(base, 0.4) }}></i>
      <div style={{ fontSize: 12 }}>{label}</div>
    </div>
  );
}
